import React, { useState } from 'react';

// Seguimiento de la suscripción de un negocio en el panel del administrador general
// (functions/src/suscripciones): cuándo termina la prueba, quién está para cobrar, a
// quién se le dio más tiempo, el link de pago con el mensaje para mandarle por
// WhatsApp y, cuando se suscribe, sus cobros en Mercado Pago (functions/src/suscripciones/cobros.js).
// Al cliente no le llega nada automático.

const pesos = (monto) => `$${Number(monto).toLocaleString('es-AR')}`;

export function hoyAR() {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' }).format(new Date());
}

// "2026-10-27" → "27/10/2026", sin pasar por Date (que lo correría un día por el huso).
export function fechaCorta(fecha) {
    const [anio, mes, dia] = String(fecha).split('-');
    return `${dia}/${mes}/${anio}`;
}

const dias = (n) => `${n} ${n === 1 ? 'día' : 'días'}`;

function sumarDias(fecha, n) {
    const d = new Date(`${fecha}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
}

// Día del mes en que se cobra por defecto: a principio de mes, pero no el 1, para que
// al cliente le hayan entrado los cobros del cierre del mes anterior.
const DIA_DE_COBRO = 5;
// Mercado Pago cobra los días 1 a 28 y el primer cobro tiene que ser desde pasado
// mañana (ver generarLink en functions/src/suscripciones).
const MAX_DIA_DE_COBRO = 28;
const MIN_DIAS_PRIMER_COBRO = 2;

// El próximo día 5 que esté al menos a dos días: la fecha de primer cobro que se propone.
export function proximoDiaDeCobro(hoy = hoyAR()) {
    const minimo = sumarDias(hoy, MIN_DIAS_PRIMER_COBRO);
    const [anio, mes] = hoy.split('-').map(Number);
    const deEsteMes = `${anio}-${String(mes).padStart(2, '0')}-${String(DIA_DE_COBRO).padStart(2, '0')}`;
    if (deEsteMes >= minimo) return deEsteMes;
    return mes === 12
        ? `${anio + 1}-01-${String(DIA_DE_COBRO).padStart(2, '0')}`
        : `${anio}-${String(mes + 1).padStart(2, '0')}-${String(DIA_DE_COBRO).padStart(2, '0')}`;
}

const diaDelMes = (fecha) => Number(String(fecha).slice(8, 10));

// "2026-11-01" → "1 de noviembre"
export function fechaLarga(fecha) {
    return new Intl.DateTimeFormat('es-AR', { day: 'numeric', month: 'long', timeZone: 'UTC' })
        .format(new Date(`${fecha}T12:00:00Z`));
}

// El nombre de pila del titular de la cuenta, para saludar.
function nombreDelTitular(negocio) {
    const titular = negocio.usuarios?.find((u) => u.rol === 'principal') || negocio.usuarios?.[0];
    return (titular?.fullName || '').trim().split(/\s+/)[0] || '';
}

// La página propia (app.transferisok.com/suscripcion/…); los links de antes van directo a Mercado Pago.
const linkParaElCliente = (link) => link.url || link.init_point;

export function mensajeWhatsApp(negocio, link) {
    const nombre = nombreDelTitular(negocio);
    return [
        `Hola${nombre ? ` ${nombre}` : ''}! ¿Cómo andás? Soy Martín, de TransferisOK.`,
        '',
        `Gracias por usar TransferisOK en ${negocio.nombre}. Para seguir usándolo, la suscripción es de ${pesos(link.monto)} por mes. Te damos unos días más sin cargo: el primer cobro es el ${fechaLarga(link.cobro_desde)} y después los días ${diaDelMes(link.cobro_desde)} de cada mes.`,
        '',
        'Para dejarla activa, entrá a este link y tocá "Suscribirme con Mercado Pago" (se debita solo cada mes y la podés cancelar cuando quieras):',
        linkParaElCliente(link),
        '',
        'Cualquier duda, escribime por acá.'
    ].join('\n');
}

// Copia seleccionando un textarea oculto: anda en el mismo clic en todos los
// navegadores, incluso donde la API del portapapeles pide permiso y queda esperando.
function copiarConSeleccion(texto) {
    const area = document.createElement('textarea');
    area.value = texto;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    let ok = false;
    try {
        ok = document.execCommand('copy');
    } catch (e) {
        ok = false;
    }
    area.remove();
    return ok;
}

async function copiar(texto) {
    if (copiarConSeleccion(texto)) return true;
    try {
        await Promise.race([
            navigator.clipboard.writeText(texto),
            new Promise((_, rechazar) => setTimeout(() => rechazar(new Error('sin respuesta')), 2000))
        ]);
        return true;
    } catch (e) {
        return false;
    }
}

export function estadoSuscripcion(s) {
    if (!s) return { texto: 'Sin seguimiento', clase: 'sin' };
    if (s.estado === 'bonificada') return { texto: 'Bonificada', clase: 'bonificada' };
    if (s.estado === 'suscripta') {
        const proximo = s.mp?.proximo_cobro;
        return { texto: `Suscripta${proximo ? ` · próximo cobro el ${fechaCorta(proximo)}` : ''}`, clase: 'suscripta' };
    }
    if (s.estado === 'cobro_rechazado') {
        const ultimo = s.cobros?.find((c) => c.resultado !== 'aprobado' && c.resultado !== 'pendiente');
        return {
            texto: ultimo?.resultado === 'reintentando' && ultimo.reintenta_hasta
                ? `Cobro rechazado · reintenta hasta el ${fechaCorta(ultimo.reintenta_hasta)}`
                : 'Cobro rechazado · sin pagar',
            clase: 'rechazado'
        };
    }
    if (s.estado === 'pausada') return { texto: 'Pausada en Mercado Pago', clase: 'cobrar' };
    if (s.estado === 'cancelada') return { texto: 'Cancelada en Mercado Pago', clase: 'rechazado' };
    if (s.estado === 'link_listo') {
        return { texto: `Link listo · cobra desde el ${fechaCorta(s.link?.cobro_desde)}`, clase: 'link' };
    }
    if (s.estado === 'por_cobrar') {
        const vencio = -s.dias_restantes;
        return {
            texto: vencio > 0 ? `Para cobrar · venció hace ${dias(vencio)}` : 'Para cobrar · vence hoy',
            clase: 'cobrar'
        };
    }
    return { texto: `En prueba · ${s.dias_restantes > 0 ? `quedan ${dias(s.dias_restantes)}` : 'vence hoy'}`, clase: 'prueba' };
}

// Orden del panel: primero los que necesitan a Martín (cobro rechazado o cancelada,
// para cobrar con el más atrasado arriba, pausada), después los que están en prueba
// (el más próximo a vencer arriba), los que tienen el link sin usar, los suscriptos,
// los bonificados y al final los que no tienen seguimiento.
const PRIORIDAD = { cobro_rechazado: 0, cancelada: 0, por_cobrar: 1, pausada: 1, prueba: 2, link_listo: 3, suscripta: 4, bonificada: 5 };
// Los que esperan algo de Martín: el número rojo del botón Suscripciones.
export const necesitaAtencion = (s) => ['por_cobrar', 'cobro_rechazado', 'cancelada', 'pausada'].includes(s?.estado);
export function ordenSuscripciones(a, b) {
    const pa = a.suscripcion ? PRIORIDAD[a.suscripcion.estado] ?? 6 : 7;
    const pb = b.suscripcion ? PRIORIDAD[b.suscripcion.estado] ?? 6 : 7;
    if (pa !== pb) return pa - pb;
    if (a.suscripcion && b.suscripcion) return a.suscripcion.dias_restantes - b.suscripcion.dias_restantes;
    return a.nombre.localeCompare(b.nombre);
}

// Fecha del primer cobro y email de la cuenta de Mercado Pago con que va a pagar.
// Propone siempre el próximo día de cobro, también al rehacer un link de otra fecha.
function FormularioLink({ negocio, regenerar, onGenerar, onCancelar }) {
    const hoy = hoyAR();
    const [cobroDesde, setCobroDesde] = useState(() => proximoDiaDeCobro(hoy));
    const [email, setEmail] = useState(negocio.suscripcion?.link?.email || negocio.mp?.email || '');
    const [ocupado, setOcupado] = useState(false);
    const [error, setError] = useState(null);

    const minimo = sumarDias(hoy, MIN_DIAS_PRIMER_COBRO);
    const diaValido = diaDelMes(cobroDesde) <= MAX_DIA_DE_COBRO;
    const valido = cobroDesde >= minimo && diaValido && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

    return (
        <form
            className="suscripcion suscripcion--editando"
            onSubmit={async (e) => {
                e.preventDefault();
                if (!valido) return;
                setOcupado(true);
                setError(null);
                try {
                    await onGenerar({ cobro_desde: cobroDesde, email: email.trim(), regenerar });
                } catch (err) {
                    setError(err.message);
                    setOcupado(false);
                }
            }}
        >
            <label className="suscripcion__campo">
                <span>Primer cobro el</span>
                <input
                    type="date"
                    className="suscripcion__fecha"
                    min={minimo}
                    value={cobroDesde}
                    onChange={(e) => setCobroDesde(e.target.value)}
                    disabled={ocupado}
                />
            </label>
            <label className="suscripcion__campo suscripcion__campo--ancho">
                <span>Email de su Mercado Pago</span>
                <input
                    type="email"
                    className="suscripcion__fecha suscripcion__email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={ocupado}
                />
            </label>
            <span className="suscripcion__ayuda">
                {diaValido
                    ? `Cobra los días ${diaDelMes(cobroDesde)} de cada mes. `
                    : `Mercado Pago cobra los días 1 a ${MAX_DIA_DE_COBRO}: elegí otro día. `}
                {regenerar
                    ? 'Se cancela el link anterior en Mercado Pago y se arma uno nuevo.'
                    : `Se crea la suscripción de ${pesos(negocio.suscripcion?.monto || 29000)}/mes en tu Mercado Pago. Al cliente no le llega nada hasta que le mandes el link.`}
            </span>
            <button type="submit" className="vista-como__boton" disabled={ocupado || !valido}>
                {ocupado ? 'Generando…' : regenerar ? 'Rehacer link' : 'Generar link'}
            </button>
            <button type="button" className="vista-como__limite-link" onClick={onCancelar} disabled={ocupado}>
                Cancelar
            </button>
            {error && <span className="vista-como__limite-error">{error}</span>}
        </form>
    );
}

// El link ya armado: el mensaje de WhatsApp para revisar, copiar y mandar.
function LinkDePago({ negocio, link, onRehacer }) {
    const [mensaje, setMensaje] = useState(() => mensajeWhatsApp(negocio, link));
    const [copiado, setCopiado] = useState(null);

    const copiarYAvisar = async (que, texto) => {
        setCopiado((await copiar(texto)) ? que : 'error');
        setTimeout(() => setCopiado(null), 2500);
    };

    return (
        <div className="suscripcion__link">
            <span className="suscripcion__detalle">
                {pesos(link.monto)}/mes · primer cobro el {fechaCorta(link.cobro_desde)}, después los días {diaDelMes(link.cobro_desde)} · paga con {link.email}
            </span>
            <textarea
                className="suscripcion__mensaje"
                rows={8}
                value={mensaje}
                onChange={(e) => setMensaje(e.target.value)}
                aria-label="Mensaje de WhatsApp"
            />
            <span className="suscripcion__acciones">
                <button className="vista-como__boton" onClick={() => copiarYAvisar('mensaje', mensaje)}>
                    {copiado === 'mensaje' ? '¡Copiado!' : 'Copiar mensaje'}
                </button>
                <button className="vista-como__boton" onClick={() => copiarYAvisar('link', linkParaElCliente(link))}>
                    {copiado === 'link' ? '¡Copiado!' : 'Copiar link'}
                </button>
                <a className="vista-como__limite-link" href={linkParaElCliente(link)} target="_blank" rel="noopener noreferrer">
                    Abrir link
                </a>
                <button className="vista-como__limite-link" onClick={onRehacer}>Rehacer link</button>
            </span>
            {copiado === 'error' && (
                <span className="vista-como__limite-error">No se pudo copiar: seleccioná el texto y copialo a mano.</span>
            )}
        </div>
    );
}

const TEXTO_COBRO = {
    aprobado: () => 'Cobrado',
    reintentando: (c) => `Rechazado: ${c.motivo}${c.reintenta_hasta ? ` · reintenta hasta el ${fechaCorta(c.reintenta_hasta)}` : ''}`,
    rechazado: (c) => `No se pudo cobrar: ${c.motivo}`,
    cancelado: () => 'Cancelado',
    pendiente: () => 'En proceso',
};

// Ya suscripto (o que lo estuvo): los cobros que hizo o intentó Mercado Pago.
function CobrosDeMp({ s, onRehacer }) {
    return (
        <div className="suscripcion__link">
            <span className="suscripcion__detalle">
                {pesos(s.link.monto)}/mes · los días {diaDelMes(s.link.cobro_desde)} · paga con {s.link.email}
            </span>
            {s.cobros?.length > 0 ? (
                <ul className="suscripcion__cobros">
                    {s.cobros.map((c) => (
                        <li key={`${c.fecha}-${c.resultado}`} className={`suscripcion__cobro suscripcion__cobro--${c.resultado}`}>
                            <span>{fechaCorta(c.fecha)}</span>
                            <span>{pesos(c.monto)}</span>
                            <span>{TEXTO_COBRO[c.resultado]?.(c) || c.resultado}</span>
                        </li>
                    ))}
                </ul>
            ) : (
                <span className="suscripcion__ayuda">Todavía no hubo cobros.</span>
            )}
            {s.estado === 'cancelada' && (
                <span className="suscripcion__acciones">
                    <button className="vista-como__boton suscripcion__boton-link" onClick={onRehacer}>Generar link nuevo</button>
                </span>
            )}
        </div>
    );
}

const ESTADOS_CON_COBROS = ['suscripta', 'cobro_rechazado', 'pausada', 'cancelada'];

function SuscripcionNegocio({ negocio, onAccion }) {
    const s = negocio.suscripcion;
    const [fecha, setFecha] = useState(null); // null = sin el formulario de fecha abierto
    const [armandoLink, setArmandoLink] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [error, setError] = useState(null);

    const hacer = async (accion, datos = {}) => {
        setOcupado(true);
        setError(null);
        try {
            await onAccion(negocio.collector_id, accion, datos);
            setFecha(null);
        } catch (e) {
            setError(e.message);
        } finally {
            setOcupado(false);
        }
    };

    const estado = estadoSuscripcion(s);
    const hoy = hoyAR();

    if (armandoLink) {
        return (
            <FormularioLink
                negocio={negocio}
                regenerar={Boolean(s?.link)}
                onCancelar={() => setArmandoLink(false)}
                onGenerar={async (datos) => {
                    await onAccion(negocio.collector_id, 'generar_link', datos);
                    setArmandoLink(false);
                }}
            />
        );
    }

    const botonLink = (
        <button className="vista-como__boton suscripcion__boton-link" onClick={() => setArmandoLink(true)} disabled={ocupado}>
            Generar link de pago
        </button>
    );

    if (fecha !== null) {
        return (
            <form
                className="suscripcion suscripcion--editando"
                onSubmit={(e) => {
                    e.preventDefault();
                    if (fecha) hacer(s ? 'cambiar_inicio' : 'iniciar', { inicio: fecha });
                }}
            >
                <span>{s ? 'Usa TransferisOK desde' : 'Empezó a usarlo el'}</span>
                <input
                    autoFocus
                    type="date"
                    className="suscripcion__fecha"
                    max={hoy}
                    value={fecha}
                    onChange={(e) => setFecha(e.target.value)}
                    disabled={ocupado}
                />
                <button type="submit" className="vista-como__boton" disabled={ocupado || !fecha || fecha > hoy}>
                    {ocupado ? 'Guardando…' : s ? 'Guardar' : 'Empezar seguimiento'}
                </button>
                <button type="button" className="vista-como__limite-link" onClick={() => setFecha(null)} disabled={ocupado}>
                    Cancelar
                </button>
                {!s && <span className="suscripcion__ayuda">La prueba es de 7 días desde esa fecha.</span>}
                {error && <span className="vista-como__limite-error">{error}</span>}
            </form>
        );
    }

    if (!s) {
        return (
            <div className="suscripcion">
                <span className={`suscripcion__estado suscripcion__estado--${estado.clase}`}>{estado.texto}</span>
                <button className="vista-como__limite-link" onClick={() => setFecha(hoy)}>Empezar seguimiento</button>
                {botonLink}
                {error && <span className="vista-como__limite-error">{error}</span>}
            </div>
        );
    }

    return (
        <div className="suscripcion">
            <span className={`suscripcion__estado suscripcion__estado--${estado.clase}`}>{estado.texto}</span>
            {s.link && (
                <button className="vista-como__limite-link" onClick={() => hacer('conciliar')} disabled={ocupado}
                    title="Vuelve a leer de Mercado Pago si se suscribió y sus cobros">
                    {ocupado ? 'Actualizando…' : 'Actualizar'}
                </button>
            )}
            {!s.link && (
                <span className="suscripcion__detalle">
                    Desde el {fechaCorta(s.inicio)}
                    {s.estado !== 'bonificada' && <> · prueba hasta el {fechaCorta(s.vence)}</>}
                    {' · '}{pesos(s.monto)}/mes
                </span>
            )}
            {/* Con el link armado, lo que queda es mandarlo (o rehacerlo si salió mal). */}
            {s.link && ESTADOS_CON_COBROS.includes(s.estado) && <CobrosDeMp s={s} onRehacer={() => setArmandoLink(true)} />}
            {s.link && !ESTADOS_CON_COBROS.includes(s.estado) && (
                <LinkDePago key={linkParaElCliente(s.link)} negocio={negocio} link={s.link} onRehacer={() => setArmandoLink(true)} />
            )}
            {!s.link && <span className="suscripcion__acciones">
                {s.estado !== 'bonificada' && botonLink}
                {s.estado !== 'bonificada' && (
                    <>
                        <button className="vista-como__boton" onClick={() => hacer('extender', { dias: 7 })} disabled={ocupado}>
                            +7 días
                        </button>
                        <button className="vista-como__boton" onClick={() => hacer('extender', { dias: 15 })} disabled={ocupado}>
                            +15 días
                        </button>
                    </>
                )}
                {s.estado === 'bonificada' ? (
                    <button className="vista-como__boton" onClick={() => hacer('quitar_bonificacion')} disabled={ocupado}>
                        Quitar bonificación
                    </button>
                ) : (
                    <button className="vista-como__boton" onClick={() => hacer('bonificar')} disabled={ocupado}>
                        Bonificar
                    </button>
                )}
                <button className="vista-como__limite-link" onClick={() => setFecha(s.inicio)} disabled={ocupado}>
                    Cambiar inicio
                </button>
            </span>}
            {error && <span className="vista-como__limite-error">{error}</span>}
        </div>
    );
}

export default SuscripcionNegocio;
